import type {
  ConfirmPriceRequest,
  ResearchBulkRequest,
  ResearchListQuery,
  ResearchResponse,
  ResearchStep,
  ResearchStepBodies,
  SaveAssessmentRequest,
} from "@pokifolio/contracts";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApi } from "../../app/api-context";

export const RESEARCH_QUERY_KEY = ["research"] as const;

/** The Research page. The previous page stays on screen while the next one loads. */
export function useResearchList(query: ResearchListQuery) {
  const api = useApi();
  return useQuery({
    queryKey: [...RESEARCH_QUERY_KEY, "list", query],
    queryFn: () => api.listResearch(query),
    placeholderData: keepPreviousData,
  });
}

/** Counts for the dashboard and the Approvals menu item; refreshed after every change to research. */
export function useResearchSummary(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...RESEARCH_QUERY_KEY, "summary"],
    queryFn: () => api.researchSummary(),
    enabled,
    staleTime: 30_000,
  });
}

/** One record and its history. */
export function useResearchRecord(id: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...RESEARCH_QUERY_KEY, "record", id],
    queryFn: () => api.getResearch(id),
    enabled,
  });
}

/** A change to one record: the record page shows the result at once, other lists refresh. */
function useRecordChange<TBody>(id: string, send: (body: TBody) => Promise<ResearchResponse>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: async (response) => {
      queryClient.setQueryData([...RESEARCH_QUERY_KEY, "record", id], response);
      await queryClient.invalidateQueries({ queryKey: RESEARCH_QUERY_KEY });
    },
    // E.g. someone else changed the record meanwhile: load their version.
    onError: () => queryClient.invalidateQueries({ queryKey: [...RESEARCH_QUERY_KEY, "record", id] }),
  });
}

/** Saves the condition check. */
export function useSaveAssessment(id: string) {
  const api = useApi();
  return useRecordChange(id, (body: SaveAssessmentRequest) => api.saveAssessment(id, body));
}

/** Confirms the price. */
export function useConfirmPrice(id: string) {
  const api = useApi();
  return useRecordChange(id, (body: ConfirmPriceRequest) => api.confirmPrice(id, body));
}

/** Any step that is one button: send for approval, withdraw, approve, reject, reopen, close. */
export type StepCall = {
  [S in ResearchStep]: { readonly step: S; readonly body: ResearchStepBodies[S] };
}[ResearchStep];

export function useResearchStep(id: string) {
  const api = useApi();
  return useRecordChange(id, (call: StepCall) => api.researchStep(id, call.step, call.body));
}

/** Deletes one record (D-58); every list and count refreshes. */
export function useDeleteResearch() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) => api.deleteResearch(id, version),
    onSuccess: (_result, { id }) => {
      queryClient.removeQueries({ queryKey: [...RESEARCH_QUERY_KEY, "record", id] });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: RESEARCH_QUERY_KEY }),
  });
}

/** Closes or deletes the chosen records; the answer says which were done and why the others were not. */
export function useResearchBulk() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ResearchBulkRequest) => api.researchBulk(body),
    onSettled: () => queryClient.invalidateQueries({ queryKey: RESEARCH_QUERY_KEY }),
  });
}

/** Which of these listings are already in Research (refreshed after every save). */
export function useListingResearch(listingIds: readonly string[], enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: [...RESEARCH_QUERY_KEY, "by-listing", listingIds],
    queryFn: () => api.researchForListings(listingIds),
    enabled: enabled && listingIds.length > 0,
    staleTime: 30_000,
  });
}

/** Save to Research. Saving a listing that is already there returns that record (created: false). */
export function useSaveResearch() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.saveResearch,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RESEARCH_QUERY_KEY }),
  });
}
